import type * as React from "react";
import { submitForm } from "./submit-form";

/**
 * The slice of the form api `<Form>` and `submitForm` need. Structural on
 * purpose: `useAppForm`'s return type carries 12 generic parameters, so naming
 * it here would force every caller to thread them. `AppForm` is declared by
 * `createFormHook` as `ComponentType<PropsWithChildren<{}>>`.
 */
export type FormHandle = {
  handleSubmit: (...args: never[]) => unknown;
  validateAllFields: (cause: "submit") => Promise<unknown>;
  validate: (cause: "submit") => unknown;
  state: { isSubmitting: boolean; canSubmit: boolean };
  AppForm: React.ComponentType<{ children?: React.ReactNode }>;
};

type FormProps = {
  form: FormHandle;
  children: React.ReactNode;
} & Omit<React.ComponentProps<"form">, "onSubmit" | "children">;

/**
 * Form wrapper that handles preventDefault and provides TanStack Form context
 * via form.AppForm.
 */
export function Form({ form, children, ...props }: FormProps) {
  return (
    <form
      // The schema is the only validator: without noValidate the browser's
      // constraint validation (type="email", required, …) swallows the submit
      // with a one-field native bubble before submitForm can reveal every
      // invalid field per the policy.
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        // Structural handle; submitForm takes the full AnyFormApi.
        void submitForm(form as never);
      }}
      {...props}
    >
      <form.AppForm>{children}</form.AppForm>
    </form>
  );
}
